<script setup lang="ts">
const Card = defineAsyncComponent(() => import("primevue/card"));

interface Capability {
  title: string;
  description: string;
  icon: string;
}

withDefaults(
  defineProps<{
    heading?: string;
    subcopy?: string;
    capabilities?: Capability[];
  }>(),
  {
    heading: "AI Capabilities",
    subcopy: "End to end, from model to production.",
    capabilities: () => [
      {
        title: "Generative AI",
        description:
          "LLM-powered features — chat, summarization, content — engineered for your product, not bolted on. We design the prompts, fine-tune the models and shape the UX so the feature feels native from day one.",
        icon: "streamline:ai-prompt-spark",
      },
      {
        title: "AI Agent Development",
        description:
          "Agent that research, act and report across your tools and data, with guardrails. We build task planning, human-in-the-loop approvals and audit trails, so every step stays observable and reversible.",
        icon: "streamline:computer-robot-cyborg-artificial-robotics-robot-intelligence-machine-technology-android",
      },
      {
        title: "RAG & Knowledge Bases",
        description:
          "Connect LLMs to your enterprise data for source-cited answers that eliminate hallucinations. We handle chunking, embeddings, permissions and re-indexing, so the knowledge base stays current and grounded.",
        icon: "streamline:interface-content-book-2-library-content-books-book-shelf-stack",
      },
      {
        title: "AI App Development",
        description:
          "Full-stack AI products — web, mobile and API — shipped by a team that builds for scale. We own the whole stack from prototype to production: UI, inference, streaming and the infrastructure behind it.",
        icon: "streamline:code-monitor-1",
      },
      {
        title: "MLOps",
        description:
          "Model deployment, monitoring and fine-tuning pipelines that keep AI reliable in production. We ship drift detection, evals and cost dashboards, so you catch regressions before your users ever do.",
        icon: "streamline:interface-setting-cog-work-loading-cog-gear-settings-machine",
      },
      {
        title: "AI Integration & APIs",
        description:
          "Drop AI into the products you already run — models, agents and RAG wired in through clean, documented APIs. We integrate with your existing stack and auth, so AI lands fast without forcing a rewrite.",
        icon: "streamline:programming-module-puzzle-1-code-puzzle-module-programming-plugin-piece",
      },
    ],
  },
);
</script>

<template>
  <section class="lg:mt-28 mt-10 relative">
    <div class="flex flex-col justify-center text-center">
      <h2>
        <WrGradientText :text="heading" />
        <span>Built Into Everything We Design and Ship</span>
      </h2>
      <p class="text-lg mt-1.5 tracking-[0.5px]">{{ subcopy }}</p>
    </div>

    <div
      class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-7 lg:mt-12 mt-7 *:w-full *:h-full"
    >
      <Reveal
        v-for="(capability, index) in capabilities"
        :key="capability.title"
        :delay="index * 100"
        append-class="h-full"
      >
        <div
          class="gradient-border-mask relative p-px dark:before:bg-[linear-gradient(91.95deg,_rgba(61,27,96,0.5)_4.5%,_rgba(63,22,104,0.5)_96.91%)] before:bg-[linear-gradient(91.95deg,_rgba(182,164,199,0.5)_4.5%,_rgba(148,103,192,0.5)_96.91%)] h-full"
        >
          <Card
            unstyled
            class="p-4 md:p-7 relative dark:bg-none bg-[var(--color-5)] dark:bg-transparent text-[var(--primary)] text-sm h-full rounded-xl"
          >
            <template #title>
              <WrIcon
                :name="capability.icon"
                class="mb-4 size-11 text-[var(--foreground)]"
              />
            </template>
            <template #content>
              <h3 class="m-0 font-semibold text-base text-black dark:text-white">
                {{ capability.title }}
              </h3>
              <p class="m-0 mt-2">{{ capability.description }}</p>
            </template>
          </Card>
        </div>
      </Reveal>
    </div>
  </section>
</template>
